import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GuidechapterItems } from '~/graphql/sdk';
import { classNames, getLinkProps } from '~/lib';
import { ArrowLeft, ArrowRight } from '../Icons';

interface ChapterNavProps {
  guideChapters: GuidechapterItems;
  currentIndex: number;
}

export const ChapterNav = ({
  guideChapters,
  currentIndex,
}: ChapterNavProps) => {
  const [isScrollable, setIsScrollable] = useState<boolean>(null);
  const [hasBeenScrolled, setHasBeenScrolled] = useState<boolean>(null);
  const [isRightScrollable, setIsRightScrollable] = useState<boolean>(null);

  const router = useRouter();
  const slug =
    typeof router?.query?.slug === 'string'
      ? router?.query?.slug
      : router?.query?.slug[0];

  useEffect(() => {
    const scrollableNav = document?.getElementById('chapters-nav');
    const currentChapter = document?.getElementById(slug);

    currentChapter.scrollIntoView();
    scrollableNav.scrollBy(50, 0);

    const scrollable = scrollableNav?.scrollWidth > scrollableNav?.clientWidth;

    setIsScrollable(scrollable);
    setIsRightScrollable(scrollable);
    scrollableNav?.scrollTo();
    window.scrollTo({ top: 0 });

    const onScroll = () => {
      setHasBeenScrolled(scrollableNav.scrollLeft > 0);

      const scrollLeftMax =
        scrollableNav?.scrollWidth - scrollableNav?.clientWidth;

      setIsRightScrollable(scrollableNav?.scrollLeft < scrollLeftMax);
    };

    scrollableNav.removeEventListener('scroll', onScroll);
    scrollableNav.addEventListener('scroll', onScroll, { passive: true });

    return () => scrollableNav.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      id="chapters-nav"
      className={classNames(
        'w-full bg-gray-100 relative top-[69px] flex items-center overflow-x-auto',
        !isScrollable && 'py-2.5',
      )}
    >
      {isScrollable && hasBeenScrolled && (
        <div className="sticky left-0 flex flex-row-reverse h-[54px] items-center z-10 justify-self-start">
          <div className="w-10 md:w-28 bg-gradient-to-r from-gray-100 h-full" />
          <button
            className="text-gray-500 w-14 px-4 bg-gray-100 flex items-center justify-center h-full"
            onClick={() =>
              document
                ?.getElementById('chapters-nav')
                ?.scrollBy({ left: -500, behavior: 'smooth' })
            }
          >
            <ArrowLeft className="w-3" />
          </button>
        </div>
      )}
      {guideChapters?.items?.map((chapter, i) => (
        <a
          key={chapter?.uuid}
          id={chapter?.slug}
          href={getLinkProps(chapter?.slug)}
          className={classNames(
            'rounded text-sm py-2 px-4 min-w-max text-gray-700 relative z-0 whitespace-nowrap',
            i === currentIndex && 'bg-white font-bold',
            hasBeenScrolled ? 'right-10 md:right-32' : 'left-8 md:left-10',
          )}
        >
          {chapter?.content?.short_title || chapter?.content?.title}
        </a>
      ))}
      {isRightScrollable && (
        <div className="sticky right-0 flex h-[54px] items-center z-10 justify-self-end">
          <div className="w-10 md:w-28 bg-gradient-to-l from-gray-100 h-full" />
          <button
            className="text-gray-500 w-14 px-4 bg-gray-100 flex items-center justify-center h-full"
            onClick={(e) =>
              document
                .getElementById('chapters-nav')
                .scrollBy({ left: 500, behavior: 'smooth' })
            }
          >
            <ArrowRight className="w-3" />
          </button>
        </div>
      )}
    </div>
  );
};
